import { closeHeaderContact } from "./header-contact";
import { closeLangSwitcher } from "./lang-switcher";
import { setLogoExpanded } from "./ma-logo-scroll";
import { setProjectsView } from "./projects-view";

const STORAGE_KEY = "modulo-projects-view";

function getNav() {
  return document.querySelector(".js-primary-nav");
}

function getPanel() {
  return document.querySelector(".js-mobile-nav-panel");
}

function getToggle() {
  return document.querySelector(".js-mobile-nav-toggle");
}

function getSiteHeader() {
  return document.querySelector(".js-header");
}

function isContactOpen() {
  return Boolean(document.querySelector(".js-header-contact.is-open"));
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

let closeTransitionHandler = null;

function clearCloseTransition(panel) {
  if (closeTransitionHandler && panel) {
    panel.removeEventListener("transitionend", closeTransitionHandler);
  }
  closeTransitionHandler = null;
}

function syncIndexActiveState() {
  const indexLink = document.querySelector(".js-projects-index-nav");
  const selectedLink = document.querySelector(
    '.c-primary-nav__panel-link[data-projects-view="selected"]'
  );

  const onProjects = document.body.classList.contains("page-projects");
  let view = null;

  try {
    view = sessionStorage.getItem(STORAGE_KEY);
  } catch (error) {
    view = null;
  }

  const isIndex =
    onProjects &&
    (document.body.getAttribute("data-current-view") === "index" ||
      view === "index");

  if (indexLink) {
    indexLink.classList.toggle("is-active", Boolean(isIndex));
    if (isIndex) {
      indexLink.setAttribute("aria-current", "page");
    } else {
      indexLink.removeAttribute("aria-current");
    }
  }

  if (selectedLink && onProjects) {
    selectedLink.classList.toggle("is-active", !isIndex);
    if (!isIndex) {
      selectedLink.setAttribute("aria-current", "page");
    } else {
      selectedLink.removeAttribute("aria-current");
    }
  }
}

function setMobileNavOpen(open) {
  const nav = getNav();
  const panel = getPanel();
  const toggle = getToggle();
  const header = getSiteHeader();

  if (!nav || !panel || !toggle) return;

  clearCloseTransition(panel);

  if (open) {
    panel.removeAttribute("hidden");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        nav.classList.add("is-open");
      });
    });
  } else {
    nav.classList.remove("is-open");

    const finishClose = () => {
      clearCloseTransition(panel);
      panel.setAttribute("hidden", "");
    };

    if (prefersReducedMotion()) {
      finishClose();
    } else {
      closeTransitionHandler = (event) => {
        if (event.target !== panel) return;
        if (event.propertyName !== "height") return;
        finishClose();
      };
      panel.addEventListener("transitionend", closeTransitionHandler);
    }
  }

  // Overlay/home styles key off this; solid headers ignore it in CSS.
  header?.classList.toggle("is-menu-open", open);

  // Reveal full resting logo while menu is open (same as Contact).
  if (!isContactOpen()) {
    setLogoExpanded(open);
  }

  const openLabel = toggle.dataset.labelOpen || "Menu";
  const closeLabel = toggle.dataset.labelClose || "Close";
  const label = open ? closeLabel : openLabel;

  toggle.textContent = label;
  toggle.setAttribute("aria-label", label);
  toggle.setAttribute("aria-expanded", open ? "true" : "false");
  toggle.classList.toggle("is-active", open);

  document.body.classList.toggle("is-mobile-nav-open", open);
}

export function isMobileNavOpen() {
  const nav = getNav();
  return Boolean(nav && nav.classList.contains("is-open"));
}

export function closeMobileNav() {
  if (isMobileNavOpen()) {
    setMobileNavOpen(false);
  }
}

export function initMobileNavMenu() {
  if (window.mobileNavMenuInitialized) {
    syncIndexActiveState();
    return;
  }

  window.mobileNavMenuInitialized = true;

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest(".js-mobile-nav-toggle");
    const nav = getNav();
    const panel = getPanel();

    if (toggle) {
      event.preventDefault();
      event.stopPropagation();

      const willOpen = !isMobileNavOpen();

      if (willOpen) {
        closeHeaderContact();
        closeLangSwitcher();
      }

      setMobileNavOpen(willOpen);
      return;
    }

    const projectsViewLink = event.target.closest("[data-projects-view]");
    if (projectsViewLink) {
      const view = projectsViewLink.getAttribute("data-projects-view");
      if (view === "index" || view === "selected") {
        try {
          sessionStorage.setItem(STORAGE_KEY, view);
        } catch (error) {
          // Ignore storage errors.
        }

        // Same-URL Swup visits only scroll (link:self) and skip re-init.
        // Apply the stored view immediately when projects markup is already present.
        if (document.querySelector(".js-projects-view")) {
          setProjectsView(view);
          syncIndexActiveState();
        }
      }
    }

    // Close after in-panel navigation / contact toggle
    if (
      isMobileNavOpen() &&
      event.target.closest(".js-mobile-nav-panel a, .js-mobile-nav-panel .js-contact-toggle")
    ) {
      setMobileNavOpen(false);
    }

    if (
      panel &&
      isMobileNavOpen() &&
      !event.target.closest(".c-site-header")
    ) {
      setMobileNavOpen(false);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeMobileNav();
    }
  });

  syncIndexActiveState();
}

export function refreshMobileNav() {
  syncIndexActiveState();
  if (!getPanel() || !getToggle()) return;
  // Keep closed after Swup content replace; logo state handled by contact/menu.
  if (isMobileNavOpen()) {
    setMobileNavOpen(false);
  }
}
